import styled from '@emotion/styled';
import { css } from '@emotion/css';
import { Alert, Modal, ModalProps } from 'antd';
import { permissionNameIdMap } from 'hooks/business/useNewPermisson';
import { ReactNode, memo, useEffect, useMemo, useRef } from 'react';
import { useTranslate } from '@tolgee/react';
import { noop } from 'lodash-es';
import {
  gatherAideTypeMap,
  isSingleScenarioType,
  scenarioPermissionMap,
  scenarioTypeGatherMap,
  scenarioTypeI18nLabelMap,
} from 'views/Aide/Scenario/config';
import { Task as OutstockTask } from 'views/Aide/OutStock/Task';
import { Task as SignalTask } from 'views/Aide/Basis/Signal/Task';
import { PolicyComponent } from 'views/Aide/Scenario/Policy';
import { PolicyComponent as NewPolicyComponent } from 'views/Aide/Scenario/NewPolicy';
import MarketingTask from './MarketingTask';
import NightWatchPolicy from './NightWatchPolicy';
import Neuron from './Neuron';
import { ScenarioDetailContent, ScenarioDetailHeader } from 'views/PureLLM/ScenarioDetailModal';
import ScenarioTitle from './ScenarioTitle';
import { KnowledgeContextProvider } from '@knowledge/knowledges/general/KnowledgeContext';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { ReactScrollProvider } from 'views/Aide/components/ReactScrollContext';
import InitiativePolicy from './InitiativePolicy';
import useCrud from 'hooks/useCrud';
import { storeUsers } from 'services/api/admin';
import { useAuthorization } from 'components/Authorization';
import ItemKnowledge from './ItemKnowledge';

export const Container = styled.section`
  padding: 16px;
  background: #f0f2f5;
  overflow: auto;
`;

const padding = 64;

export const modalClassName = css`
  top: ${padding}px;
  padding: 0;
  min-width: 1000px;
  width: 80vw;
  .ant-modal-content {
    background: transparent;
    box-shadow: none;
  }
  .ant-modal-body {
    display: flex;
    flex-direction: column;
    height: calc(100vh - ${padding * 2 + 55}px);
    padding: 0;
    > div {
      flex: 0 0 auto;
    }
    > section {
      flex: 1 0 0;
    }
  }
`;

export interface ModalMeta {
  message: Conversation.Message;
  botReplyType: Conversation.BotReplyType;
  extraInfo: Conversation.ExtraInfo;
  spuId?: string;
}

export interface ScenarioModalProps extends ModalProps, ModalMeta {}

function ScenarioModal(props: ScenarioModalProps) {
  const { message, botReplyType, extraInfo, spuId, ...otherProps } = props;

  const { onCancel } = otherProps;

  const { t } = useTranslate();

  const { payload } = useAuthorization();

  const { whiteListConfig: { AIDE_REMOVE_TASK } = {} } = useOrgConfigStateValue();

  const { channel: platform, region, storeId } = message;

  const {
    scenario: scenarioType,
    scenarioId,
    policyId,
    fallbackPolicyId,
    roundId,
    botMarketingType,
    storeGroupId,
    isSizeEnquire = false,
    replyOption = 'DEFAULT',
    llmbasicInfo,
  } = extraInfo;

  const isMarketing = botMarketingType !== 'BMT_UNKNOWN';

  const permissionName = useMemo<General.PermissionName | undefined>(() => {
    if (spuId) {
      return undefined;
    }

    if (!isMarketing) {
      return 'AI智能接待';
    }

    return scenarioPermissionMap[scenarioType];
  }, [isMarketing, scenarioType, spuId]);

  const {
    list: userList = [],
    fetchList: fetchUserList,
    setList: setUserList,
  } = useCrud({
    readList: storeUsers,
  });

  const refsCurrent = { fetchUserList, setUserList };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    if (permissionName) {
      refs.current
        .fetchUserList({
          storeId,
          permId: permissionNameIdMap[permissionName],
        })
        .catch(() => {});
    }

    return () => {
      refs.current.setUserList([]);
    };
  }, [storeId, permissionName]);

  const permission = useMemo(
    () => userList.some((_) => _.id === (payload as any)?.id),
    [userList, payload],
  );

  if (platform === 'WA_BIZ') {
    return null;
  }

  const tips = !permission && userList.length > 0 && (
    <Alert
      banner
      type="warning"
      message={t('scene.chatWorkspace.aiReception.permission.noConfigPrivilege', {
        contactPerson: userList.map(({ name, account }) => `[${name || account}]`).join(' '),
      })}
    />
  );

  const getModal = (title: ReactNode, content: ReactNode, containerStyle?: React.CSSProperties) => {
    return (
      <Modal title={title} width="80vw" footer={false} className={modalClassName} {...otherProps}>
        {tips}
        <ReactScrollProvider>
          <Container style={containerStyle}>{content}</Container>
        </ReactScrollProvider>
      </Modal>
    );
  };

  if (llmbasicInfo?.llmIntentId) {
    return getModal(
      <ScenarioDetailHeader
        enabled={!!otherProps.visible}
        region={region}
        storeId={storeId}
        storeGroupId={extraInfo.storeGroupId != null ? String(extraInfo.storeGroupId) : undefined}
        intentId={llmbasicInfo.llmIntentId}
      />,
      <ScenarioDetailContent
        enabled={!!otherProps.visible && !!llmbasicInfo.llmIntentId}
        editable={permission}
        region={region}
        storeId={storeId}
        storeGroupId={extraInfo.storeGroupId != null ? String(extraInfo.storeGroupId) : undefined}
        storeGroupName={extraInfo.storeGroupName}
        intentId={llmbasicInfo.llmIntentId}
        policyId={llmbasicInfo.llmPolicyId}
        subPolicyId={llmbasicInfo.llmSubPolicyId}
        hideDetailTitleBar
      />,
      { background: '#fff', padding: '0' },
    );
  }

  if (spuId) {
    return (
      <ItemKnowledge spuId={spuId}>
        {(content, name) => {
          return getModal(
            <div className="flex">
              {t('scene.chatWorkspace.aiReception.productKnowledge')}
              {name && '-'}
              {name}
            </div>,
            content,
          );
        }}
      </ItemKnowledge>
    );
  }

  if (!isMarketing) {
    if (isSizeEnquire) {
      return getModal(
        t('domain.aiReception.type.sizeRecommendation'),
        <Neuron
          key={scenarioType}
          path="qa/scenarios/charts"
          storeId={storeId}
          storeGroupId={storeGroupId}
          platform={platform}
          region={region}
          editable={permission}
          activeTemplateName={scenarioType}
        />,
      );
    }

    if (botReplyType === 'GUARANTEED_ANSWER') {
      return getModal(
        t('scene.chatWorkspace.aiReception.onDuty.settings'),
        <NightWatchPolicy
          key={fallbackPolicyId}
          platform={platform}
          region={region}
          policyId={fallbackPolicyId}
          storeGroupId={storeGroupId}
          editable={permission}
          onClose={onCancel}
        />,
      );
    }

    return getModal(
      <ScenarioTitle
        key={scenarioId}
        storeGroupId={storeGroupId}
        scenarioId={scenarioId}
        fallback={t('domain.aiReception.type.aiReception')}
      />,
      <Neuron
        key={policyId}
        path="qa/scenarios/packages"
        storeId={storeId}
        storeGroupId={storeGroupId}
        id={scenarioId}
        policyId={policyId}
        platform={platform}
        region={region}
        editable={permission}
        transparent
      />,
    );
  }

  if (botMarketingType === 'BMT_INITIATIVE_TASK') {
    return getModal(
      t('scene.chatWorkspace.aiReception.scenarioPreview.action.contactBuyer'),
      <InitiativePolicy
        key={policyId}
        policyId={policyId}
        platform={platform}
        region={region}
        onBack={onCancel || noop}
      />,
    );
  }

  const scenarioTitle = t(scenarioTypeI18nLabelMap[scenarioType]);

  const gather = isSingleScenarioType(scenarioType)
    ? undefined
    : scenarioTypeGatherMap[scenarioType];

  const action = permission ? 'update' : 'view';

  if (scenarioType === 'OUT_STOCK_REMINDER' || scenarioType === 'ORDER_FOLLOWUP') {
    return getModal(
      scenarioTitle,
      <OutstockTask
        key={scenarioId}
        routeMode={false}
        scenarioType={scenarioType}
        value={{
          platform,
          id: `${scenarioId}`,
          action,
        }}
      />,
    );
  }

  if (scenarioType === 'SIGNAL_COUPON') {
    return getModal(
      scenarioTitle,
      <SignalTask
        key={scenarioId}
        routeMode={false}
        value={{
          platform,
          id: `${scenarioId}`,
          action,
        }}
      />,
    );
  }

  if (AIDE_REMOVE_TASK) {
    return getModal(
      scenarioTitle,
      <NewPolicyComponent
        key={policyId}
        routeMode={false}
        value={{
          scenarioType,
          platform,
          region,
          id: policyId,
          replyOption,
          round: 1, // 只在创建时用到，所以可以随便填。
          action,
        }}
        onChange={noop}
        onBack={onCancel || noop}
        defaultActiveRegion={region}
      />,
    );
  }

  if (scenarioType === 'RECEPTION_SCORE') {
    return getModal(
      scenarioTitle,
      <PolicyComponent
        key={`${policyId}-${replyOption}`}
        routeMode={false}
        value={{
          scenarioType,
          platform,
          region,
          id: policyId,
          replyOption,
          roundId,
          action,
        }}
        onBack={onCancel || noop}
        defaultActiveRegion={region}
      />,
    );
  }

  const aideType = gather ? gatherAideTypeMap[gather] : undefined;

  return getModal(
    scenarioTitle,
    <MarketingTask
      key={policyId}
      editable={permission}
      activePolicyId={policyId}
      activeRoundId={roundId}
      value={{
        platform,
        region,
        aideType,
        gather,
        scenarioType,
        taskId: `${scenarioId}`,
        roundId,
        pageNum: 1,
        pageSize: 10000,
      }}
    />,
  );
}

export default memo<ScenarioModalProps>((props) => {
  return (
    <KnowledgeContextProvider>
      <ScenarioModal {...props} />
    </KnowledgeContextProvider>
  );
});
